import { Fragment, useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
  Pencil,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { request } from "../lib/api";
import { downloadCsvTemplate } from "../lib/csvTemplates";
import { useAttribution } from "../lib/useAttribution";
import { useRowSelection } from "../lib/useRowSelection";
import { AttributionCell } from "../components/Attribution";
import { StudySetup } from "./StudySetup";
import type {
  CreatedResponse,
  ImportSchoolsResponse,
  Notice,
  StructureItem,
  StructureResponse,
} from "../types";
import {
  Badge,
  BulkActionsBar,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorState,
  InlineNotice,
  LoadingState,
  Table,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
} from "../components/ui";

const structureFields = {
  cohorts: ["code", "name", "start_date", "end_date"],
  districts: ["code", "name"],
  centers: ["district_id", "code", "name"],
  institutes: ["district_id", "center_id", "code", "name"],
  "study-groups": ["cohort_id", "internal_code", "name", "arm_kind"],
  rooms: ["center_id", "name"],
} as const;
type Entity = keyof typeof structureFields;

const defaultEntities: Entity[] = ["cohorts", "study-groups", "rooms"];

const entityDisplayNames: Record<Entity, string> = {
  cohorts: "cohorts",
  districts: "districts",
  centers: "centres",
  institutes: "schools",
  "study-groups": "study groups",
  rooms: "rooms",
};

// Deleting most of these entities cascades into PD Day sessions (and their
// attendance/progress) or other real records that a plain "permanently
// deleted" message wouldn't warn about. "centers" is the one exception —
// removing a centre just demotes the school, nothing else is touched.
const entityDeleteWarnings: Record<Entity, string> = {
  cohorts:
    "Its modules (and all their content), PD Day sessions (and their attendance/progress records) and study groups will be permanently deleted too.",
  districts:
    "Every school/centre in it will be deleted too, along with any PD Day sessions they hosted and those sessions' attendance/progress records.",
  centers: "The school stops being a centre; it still exists as a school.",
  institutes:
    "Any PD Day sessions hosted here will be deleted too, along with their attendance/progress records. Teachers assigned here are unassigned, not deleted.",
  "study-groups":
    "A group that has ever had teachers cannot be deleted: who was in which group is part of the study.",
  rooms:
    "Any PD Day sessions held in this room will be deleted too, along with their attendance/progress records.",
};

export function Structure({
  initialEntity = "cohorts",
  entities = defaultEntities,
}: {
  initialEntity?: Entity;
  entities?: Entity[];
}) {
  const [entity, setEntity] = useState<Entity>(initialEntity);
  const [message, setMessage] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<StructureItem[] | null>(null);
  const [listError, setListError] = useState("");
  const [cohorts, setCohorts] = useState<StructureItem[]>([]);
  const [districts, setDistricts] = useState<StructureItem[]>([]);
  const [centres, setCentres] = useState<StructureItem[]>([]);
  const [cohortFilter, setCohortFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  // Creating a centre: the district picked in the form, and the schools from
  // that district that will have the new centre as their home centre.
  const [formDistrict, setFormDistrict] = useState("");
  const [centreSchools, setCentreSchools] = useState<string[]>([]);
  const [districtSchools, setDistrictSchools] = useState<StructureItem[]>([]);
  const [editingItem, setEditingItem] = useState<StructureItem | null>(null);
  // The form remounts for each record, so the picker starts again with it.
  function editItem(item: StructureItem | null) {
    setEditingItem(item);
    setFormDistrict("");
    setCentreSchools([]);
  }
  const [deletingID, setDeletingID] = useState<string | null>(null);
  const [confirmItem, setConfirmItem] = useState<StructureItem | null>(null);
  const load = useCallback((current: Entity, cohortID = "") => {
    const query = current === "institutes" && cohortID ? `?cohort_id=${cohortID}` : "";
    request<StructureResponse>(`/api/admin/structure/${current}${query}`)
      .then((data) => {
        setItems(data.items);
        setListError("");
      })
      .catch((cause) => setListError(errorMessage(cause)));
  }, []);
  useEffect(() => {
    load(entity);
  }, [entity, load]);
  useEffect(() => {
    request<StructureResponse>("/api/admin/structure/cohorts")
      .then((data) => setCohorts(data.items))
      .catch(() => setCohorts([]));
    request<StructureResponse>("/api/admin/structure/districts")
      .then((data) => setDistricts(data.items))
      .catch(() => setDistricts([]));
  }, []);
  // Centres are refreshed whenever structure changes: ticking "is centre" on
  // a school adds it to every centre dropdown.
  const loadCentres = useCallback(() => {
    request<StructureResponse>("/api/admin/structure/centers")
      .then((data) => setCentres(data.items))
      .catch(() => setCentres([]));
  }, []);
  useEffect(() => {
    loadCentres();
  }, [loadCentres]);
  const loadSchools = useCallback(() => {
    request<StructureResponse>("/api/admin/structure/institutes")
      .then((data) => setDistrictSchools(data.items.filter((item) => !item.is_center)))
      .catch(() => setDistrictSchools([]));
  }, []);
  useEffect(() => {
    if (entity === "centers") loadSchools();
  }, [entity, loadSchools]);
  function cohortName(cohortID: string): string {
    return cohorts.find((cohort) => cohort.id === cohortID)?.name || cohortID;
  }
  function centreName(centreID?: string): string {
    return centres.find((centre) => centre.id === centreID)?.name || "";
  }
  function districtName(districtID?: string): string {
    return districts.find((district) => district.id === districtID)?.name || "";
  }
  function onCohortFilterChange(value: string) {
    setCohortFilter(value);
    load(entity, value);
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const form = event.currentTarget;
    const data: Record<string, FormDataEntryValue | null | object> = Object.fromEntries(
      new FormData(form)
    );
    for (const key of Object.keys(data)) if (data[key] === "") data[key] = null;
    if (entity === "cohorts") data.cohort_properties = {};
    if (entity === "centers" && !editingItem) {
      if (centreSchools.length === 0) {
        setMessage({ tone: "error", text: "Select at least one school this centre hosts." });
        setBusy(false);
        return;
      }
      data.school_ids = centreSchools;
    }
    try {
      if (editingItem) {
        await request(`/api/admin/structure/${entity}/${editingItem.id}`, {
          method: "PUT",
          body: JSON.stringify(data),
        });
        setMessage({ tone: "success", text: `Updated ${entityDisplayNames[entity]}.` });
        editItem(null);
      } else {
        const result = await request<CreatedResponse>(`/api/admin/structure/${entity}`, {
          method: "POST",
          body: JSON.stringify(data),
        });
        setMessage({
          tone: "success",
          text: `Created ${entityDisplayNames[entity]}: ${result.id}`,
        });
      }
      form.reset();
      setFormDistrict("");
      setCentreSchools([]);
      load(entity, cohortFilter);
      loadCentres();
      if (entity === "centers") loadSchools();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }
  async function remove(item: StructureItem) {
    setDeletingID(item.id);
    setMessage(null);
    try {
      await request(`/api/admin/structure/${entity}/${item.id}`, { method: "DELETE" });
      setMessage({ tone: "success", text: `Deleted ${entityDisplayNames[entity]}.` });
      if (editingItem?.id === item.id) editItem(null);
      load(entity, cohortFilter);
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setDeletingID(null);
      setConfirmItem(null);
    }
  }
  const visibleItems =
    entity === "institutes"
      ? items?.filter(
          (item) => !item.is_center && (!districtFilter || item.district_id === districtFilter)
        ) || null
      : items;
  const { selection, selectedIds, clear } = useRowSelection(
    (visibleItems || []).map((item) => item.id)
  );
  const attribution = useAttribution(
    entity,
    (visibleItems || []).map((item) => item.id)
  );
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);
  async function bulkDelete() {
    setBulkDeleting(true);
    setMessage(null);
    try {
      await request(`/api/admin/structure/${entity}/bulk-delete`, {
        method: "POST",
        body: JSON.stringify({ ids: [...selectedIds] }),
      });
      setMessage({
        tone: "success",
        text: `Deleted ${selectedIds.size} ${entityDisplayNames[entity]}.`,
      });
      setConfirmingBulkDelete(false);
      clear();
      load(entity, cohortFilter);
      loadCentres();
    } catch (cause) {
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBulkDeleting(false);
    }
  }
  const singleEntity = entities.length === 1;
  return (
    <div className={singleEntity ? "" : "grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]"}>
      {!singleEntity && (
        <Card title="Structure type" text="Choose what to add">
          <div className="grid gap-1">
            {entities.map((name) => (
              <button
                key={name}
                type="button"
                className={`flex items-center justify-between rounded-md px-3 py-2.5 text-left text-sm font-medium capitalize transition-colors ${entity === name ? "bg-interactive/10 text-interactive" : "text-content-secondary hover:bg-surface-muted hover:text-content-primary"}`}
                onClick={() => {
                  setEntity(name);
                  setMessage(null);
                  setCohortFilter("");
                  editItem(null);
                  setConfirmItem(null);
                  clear();
                }}
              >
                {entityDisplayNames[name]}
                <ChevronRight size={15} />
              </button>
            ))}
          </div>
        </Card>
      )}
      <div>
        <div
          className={entity === "institutes" ? "grid gap-6 min-[1800px]:grid-cols-[minmax(0,1fr)_380px]" : ""}
        >
          <Card
            title={`${editingItem ? "Edit" : "Create"} ${entityDisplayNames[entity].replace(/s$/, "")}`}
            text="Use stable codes and IDs so records remain consistent across imports and exports."
          >
            <form
              className="grid gap-5 sm:grid-cols-2"
              onSubmit={submit}
              key={editingItem?.id || "new"}
            >
              {structureFields[entity].map((name) =>
                name === "district_id" ? (
                  <Fragment key={name}>
                    <label className={labelClass}>
                      District
                      <select
                        className={inputClass}
                        name="district_id"
                        required
                        defaultValue={editingItem?.district_id || ""}
                        onChange={(event) => {
                          setFormDistrict(event.target.value);
                          setCentreSchools([]);
                        }}
                      >
                        <option value="">Select district</option>
                        {districts.map((district) => (
                          <option key={district.id} value={district.id}>
                            {district.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    {entity === "centers" && !editingItem && (
                      <SchoolMultiSelect
                        schools={districtSchools.filter(
                          (school) => formDistrict && school.district_id === formDistrict
                        )}
                        districtChosen={Boolean(formDistrict)}
                        selected={centreSchools}
                        onChange={setCentreSchools}
                        centreName={centreName}
                      />
                    )}
                    {entity === "centers" && (
                      <label className={labelClass}>
                        {editingItem ? "Add a cohort (optional)" : "Cohort"}
                        <select
                          className={inputClass}
                          name="cohort_id"
                          required={!editingItem}
                          defaultValue=""
                        >
                          <option value="">
                            {editingItem ? "No new cohort" : "Select cohort"}
                          </option>
                          {cohorts
                            .filter((cohort) => !editingItem?.cohort_ids?.includes(cohort.id))
                            .map((cohort) => (
                              <option key={cohort.id} value={cohort.id}>
                                {cohort.name}
                              </option>
                            ))}
                        </select>
                        <span className="text-xs font-normal text-content-secondary">
                          The centre and its schools are linked to it; schools added later get it
                          too.
                        </span>
                      </label>
                    )}
                  </Fragment>
                ) : name === "arm_kind" ? (
                  <label className={labelClass} key={name}>
                    Treatment or control
                    <select
                      className={inputClass}
                      name="arm_kind"
                      defaultValue={editingItem?.arm_kind || ""}
                    >
                      <option value="">Not decided yet</option>
                      <option value="treatment">Treatment</option>
                      <option value="control">Control</option>
                    </select>
                    <span className="text-xs font-normal text-content-secondary">
                      A label for analysis only. It never changes what anyone sees. Teachers cannot
                      be put in a group until it is set.
                    </span>
                  </label>
                ) : name === "cohort_id" && entity === "study-groups" ? (
                  <label className={labelClass} key={name}>
                    Cohort
                    <select
                      className={inputClass}
                      name="cohort_id"
                      required
                      defaultValue={editingItem?.cohort_id || ""}
                    >
                      <option value="">Select cohort</option>
                      {cohorts.map((cohort) => (
                        <option key={cohort.id} value={cohort.id}>
                          {cohort.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : name === "center_id" ? (
                  <label className={labelClass} key={name}>
                    {entity === "rooms" ? "Centre" : "Home centre (optional)"}
                    <select
                      className={inputClass}
                      name="center_id"
                      required={entity === "rooms"}
                      defaultValue={editingItem?.center_id || ""}
                    >
                      <option value="">
                        {entity === "rooms" ? "Select centre" : "No home centre"}
                      </option>
                      {centres
                        .filter((centre) => centre.id !== editingItem?.id)
                        .map((centre) => (
                          <option key={centre.id} value={centre.id}>
                            {centre.name}
                          </option>
                        ))}
                    </select>
                    {centres.length === 0 && (
                      <span className="text-xs font-normal text-content-secondary">
                        No centres yet. Create one on the Centres page first.
                      </span>
                    )}
                  </label>
                ) : (
                  <label className={labelClass} key={name}>
                    <span className="capitalize">{name.replaceAll("_", " ")}</span>
                    <input
                      className={inputClass}
                      name={name}
                      type={name.endsWith("date") ? "date" : "text"}
                      required={!name.endsWith("date")}
                      defaultValue={
                        name.endsWith("date")
                          ? ((editingItem?.[name as keyof StructureItem] as string) || "").slice(
                              0,
                              10
                            )
                          : (editingItem?.[name as keyof StructureItem] as string) || ""
                      }
                    />
                  </label>
                )
              )}
              <div className="flex items-center gap-3 border-t border-border pt-5 sm:col-span-2">
                <button className={primaryButtonClass} disabled={busy}>
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  {editingItem
                    ? "Save changes"
                    : `Create ${entityDisplayNames[entity].replace(/s$/, "")}`}
                </button>
                {editingItem && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-content-secondary hover:text-content-primary"
                    onClick={() => editItem(null)}
                  >
                    <X size={14} />
                    Cancel
                  </button>
                )}
                <span className="text-xs text-content-secondary">
                  Fields marked by browser validation are required.
                </span>
              </div>
            </form>
            {message && <InlineNotice tone={message.tone} text={message.text} breakAll />}
          </Card>
          {entity === "institutes" && (
            <BulkImportSchools onImported={() => load(entity, cohortFilter)} />
          )}
        </div>
        <Card title={entityDisplayNames[entity]} text="Existing records for this structure type.">
          {entity === "institutes" && (
            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:max-w-2xl">
              <label className={labelClass}>
                District
                <select
                  className={inputClass}
                  value={districtFilter}
                  onChange={(event) => {
                    setDistrictFilter(event.target.value);
                    clear();
                  }}
                >
                  <option value="">All districts</option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className={labelClass}>
                Cohort (optional)
                <select
                  className={inputClass}
                  value={cohortFilter}
                  onChange={(event) => {
                    onCohortFilterChange(event.target.value);
                    clear();
                  }}
                >
                  <option value="">All cohorts</option>
                  {cohorts.map((cohort) => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <BulkActionsBar
            count={selectedIds.size}
            onDelete={() => setConfirmingBulkDelete(true)}
            onClear={clear}
            busy={bulkDeleting}
          />
          {listError ? (
            <ErrorState message={listError} />
          ) : visibleItems === null ? (
            <LoadingState message="Loading…" />
          ) : !visibleItems.length ? (
            <EmptyState title="No records yet" message="Create one using the form above." />
          ) : entity === "institutes" ? (
            <Table
              selection={selection}
              headers={[
                { label: "Code" },
                { label: "Name" },
                { label: "District", hide: "sm" },
                { label: "Centre" },
                { label: "Cohorts", hide: "md" },
                { label: "ID", hide: "md" },
                { label: "" },
                { label: "Actions" },
              ]}
              rows={visibleItems.map((item) => [
                item.code || "—",
                <span key="name">
                  {item.name}
                  <span className="mt-1 block">
                    <AttributionCell info={attribution[item.id]} />
                  </span>
                </span>,
                districtName(item.district_id) || "—",
                centreName(item.center_id) || "—",
                item.cohort_ids?.length ? item.cohort_ids.map(cohortName).join(", ") : "—",
                <code key={item.id} className="text-xs text-content-secondary">
                  {item.id}
                </code>,
                <LinkCohortAction
                  key="link"
                  instituteID={item.id}
                  cohorts={cohorts}
                  linkedIDs={item.cohort_ids ?? []}
                  onLinked={() => load(entity, cohortFilter)}
                />,
                <RowActions
                  key="actions"
                  item={item}
                  deleting={deletingID === item.id}
                  onEdit={() => editItem(item)}
                  onDelete={() => setConfirmItem(item)}
                />,
              ])}
            />
          ) : entity === "centers" ? (
            <Table
              selection={selection}
              headers={[
                { label: "Code" },
                { label: "Name" },
                { label: "District", hide: "sm" },
                { label: "Cohorts" },
                { label: "ID", hide: "md" },
                { label: "Actions" },
              ]}
              rows={visibleItems.map((item) => [
                item.code || "—",
                <span key="name">
                  {item.name}
                  <span className="mt-1 block">
                    <AttributionCell info={attribution[item.id]} />
                  </span>
                </span>,
                districtName(item.district_id) || "—",
                item.cohort_ids?.length ? item.cohort_ids.map(cohortName).join(", ") : "—",
                <code key={item.id} className="text-xs text-content-secondary">
                  {item.id}
                </code>,
                <RowActions
                  key="actions"
                  item={item}
                  deleting={deletingID === item.id}
                  onEdit={() => editItem(item)}
                  onDelete={() => setConfirmItem(item)}
                />,
              ])}
            />
          ) : (
            <Table
              selection={selection}
              headers={[
                { label: entity === "rooms" ? "Centre" : "Code" },
                { label: "Name" },
                ...(entity === "districts" ? [{ label: "Schools" }] : []),
                ...(entity === "study-groups"
                  ? [
                      { label: "Cohort", hide: "sm" as const },
                      { label: "Arm" },
                      { label: "Teachers" },
                    ]
                  : []),
                { label: "ID", hide: "md" },
                { label: "Actions" },
              ]}
              rows={visibleItems.map((item) => [
                entity === "rooms"
                  ? centreName(item.center_id) || "—"
                  : item.code || item.internal_code || "—",
                <span key="name">
                  {item.name}
                  <span className="mt-1 block">
                    <AttributionCell info={attribution[item.id]} />
                  </span>
                </span>,
                ...(entity === "districts" ? [(item.school_count ?? 0).toLocaleString()] : []),
                ...(entity === "study-groups"
                  ? [
                      cohortName(item.cohort_id || ""),
                      item.arm_kind ? (
                        <Badge key="arm" tone={item.arm_kind === "control" ? "neutral" : "success"}>
                          {item.arm_kind}
                        </Badge>
                      ) : (
                        <span key="arm" className="text-xs text-danger">
                          Not set
                        </span>
                      ),
                      (item.teacher_count ?? 0).toLocaleString(),
                    ]
                  : []),
                <code key={item.id} className="text-xs text-content-secondary">
                  {item.id}
                </code>,
                <RowActions
                  key="actions"
                  item={item}
                  deleting={deletingID === item.id}
                  onEdit={() => editItem(item)}
                  onDelete={() => setConfirmItem(item)}
                />,
              ])}
            />
          )}
        </Card>
        {entity === "study-groups" && (
          <StudySetup cohorts={cohorts} onChanged={() => load(entity, cohortFilter)} />
        )}
      </div>
      <ConfirmModal
        open={!!confirmItem}
        title={`Delete ${entityDisplayNames[entity].replace(/s$/, "")}?`}
        message={`"${confirmItem?.name}" will be permanently deleted. ${entityDeleteWarnings[entity]} This cannot be undone.`}
        confirmLabel="Delete"
        busy={!!confirmItem && deletingID === confirmItem.id}
        onConfirm={() => confirmItem && remove(confirmItem)}
        onCancel={() => setConfirmItem(null)}
      />
      <ConfirmModal
        open={confirmingBulkDelete}
        title={`Delete ${selectedIds.size} ${entityDisplayNames[entity]}?`}
        message={`These will be permanently deleted. ${entityDeleteWarnings[entity]} This cannot be undone.`}
        confirmLabel="Delete"
        busy={bulkDeleting}
        onConfirm={() => void bulkDelete()}
        onCancel={() => setConfirmingBulkDelete(false)}
      />
    </div>
  );
}

export function Schools() {
  return <Structure initialEntity="institutes" entities={["institutes"]} />;
}
export function Centres() {
  return <Structure initialEntity="centers" entities={["centers"]} />;
}
export function Districts() {
  return <Structure initialEntity="districts" entities={["districts"]} />;
}

const MAX_CENTRE_SCHOOLS = 5;

// The schools a new centre hosts PD Days for: a dropdown of its district's
// schools, one to five ticked. A school that already has a home centre moves
// to the new one.
function SchoolMultiSelect({
  schools,
  districtChosen,
  selected,
  onChange,
  centreName,
}: {
  schools: StructureItem[];
  districtChosen: boolean;
  selected: string[];
  onChange: (ids: string[]) => void;
  centreName: (id?: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const container = useRef<HTMLDivElement | null>(null);
  const full = selected.length >= MAX_CENTRE_SCHOOLS;

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: PointerEvent) {
      if (container.current && !container.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]);
  }
  const visible = schools.filter((school) =>
    school.name.toLowerCase().includes(search.trim().toLowerCase())
  );
  const chosen = schools.filter((school) => selected.includes(school.id));
  const summary = !districtChosen
    ? "Select a district first"
    : schools.length === 0
      ? "No schools in this district"
      : selected.length === 0
        ? "Select schools"
        : `${selected.length} of ${MAX_CENTRE_SCHOOLS} selected`;

  return (
    <div className={labelClass} ref={container}>
      <span>Schools hosted (1–{MAX_CENTRE_SCHOOLS})</span>
      <div className="relative">
        <button
          type="button"
          className={`${inputClass} flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60`}
          disabled={!districtChosen || schools.length === 0}
          onClick={() => setOpen((value) => !value)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={selected.length ? "" : "text-content-secondary"}>{summary}</span>
          <ChevronDown
            size={16}
            className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-border bg-white shadow-lg">
            <div className="border-b border-border p-2">
              <input
                className={inputClass}
                placeholder="Search schools"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                // Enter here filters; it must not submit the centre form.
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
                autoFocus
              />
            </div>
            <ul className="max-h-60 overflow-y-auto p-1" role="listbox" aria-multiselectable="true">
              {visible.length === 0 && (
                <li className="px-3 py-2 text-sm text-content-secondary">No matching schools</li>
              )}
              {visible.map((school) => {
                const checked = selected.includes(school.id);
                const current = centreName(school.center_id);
                return (
                  <li key={school.id} role="option" aria-selected={checked}>
                    <label
                      className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm font-normal ${!checked && full ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-surface-muted"}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={checked}
                        disabled={!checked && full}
                        onChange={() => toggle(school.id)}
                      />
                      <span>
                        {school.name}
                        {current && (
                          <span className="block text-xs text-content-secondary">
                            Currently in {current}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
            {full && (
              <p className="border-t border-border px-3 py-2 text-xs text-content-secondary">
                Maximum of {MAX_CENTRE_SCHOOLS} schools reached.
              </p>
            )}
          </div>
        )}
      </div>
      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((school) => (
            <span
              key={school.id}
              className="inline-flex items-center gap-1 rounded-full bg-surface-muted px-2.5 py-1 text-xs font-medium"
            >
              {school.name}
              <button
                type="button"
                aria-label={`Remove ${school.name}`}
                className="text-content-secondary hover:text-content-primary"
                onClick={() => toggle(school.id)}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function RowActions({
  item,
  deleting,
  onEdit,
  onDelete,
}: {
  item: StructureItem;
  deleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-content-secondary hover:text-interactive"
        onClick={onEdit}
        aria-label={`Edit ${item.name}`}
      >
        <Pencil size={16} />
      </button>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-content-secondary hover:text-danger disabled:opacity-50"
        onClick={onDelete}
        disabled={deleting}
        aria-label={`Delete ${item.name}`}
      >
        {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
      </button>
    </div>
  );
}

// Picking a cohort links it straight away; a failure is shown under the
// picker rather than silently leaving the row unchanged.
function LinkCohortAction({
  instituteID,
  cohorts,
  linkedIDs,
  onLinked,
}: {
  instituteID: string;
  cohorts: StructureItem[];
  linkedIDs: string[];
  onLinked: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function link(cohortID: string) {
    if (!cohortID) return;
    setBusy(true);
    setError("");
    try {
      await request("/api/admin/structure/institutes/link-cohort", {
        method: "POST",
        body: JSON.stringify({ institute_id: instituteID, cohort_id: cohortID }),
      });
      onLinked();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }
  const available = cohorts.filter((cohort) => !linkedIDs.includes(cohort.id));
  return (
    <div className="grid gap-1">
      <select
        className={`${inputClass} !w-auto py-1.5 text-xs`}
        value=""
        disabled={busy || available.length === 0}
        onChange={(event) => void link(event.target.value)}
        aria-label="Link to cohort"
      >
        <option value="">
          {busy ? "Linking…" : available.length ? "Link cohort…" : "All cohorts linked"}
        </option>
        {available.map((cohort) => (
          <option key={cohort.id} value={cohort.id}>
            {cohort.name}
          </option>
        ))}
      </select>
      {error && <span className="text-xs text-danger">{error}</span>}
    </div>
  );
}

function BulkImportSchools({ onImported }: { onImported: () => void }) {
  const [districts, setDistricts] = useState<StructureItem[]>([]);
  const [cohorts, setCohorts] = useState<StructureItem[]>([]);
  const [result, setResult] = useState<ImportSchoolsResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    request<StructureResponse>("/api/admin/structure/districts")
      .then((data) => setDistricts(data.items))
      .catch(() => setDistricts([]));
    request<StructureResponse>("/api/admin/structure/cohorts")
      .then((data) => setCohorts(data.items))
      .catch(() => setCohorts([]));
  }, []);
  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const value = await request<ImportSchoolsResponse>("/api/admin/import/schools", {
        method: "POST",
        body: new FormData(event.currentTarget),
      });
      setResult(value);
      onImported();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card
      title="Bulk import schools"
      text="Add multiple schools to a district and cohort in one upload."
    >
      <form className="grid gap-4" onSubmit={upload}>
        <label className={labelClass}>
          District
          <select className={inputClass} name="district_id" required>
            <option value="">Select district</option>
            {districts.map((district) => (
              <option key={district.id} value={district.id}>
                {district.name}
              </option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          Cohort
          <select className={inputClass} name="cohort_id" required>
            <option value="">Select cohort</option>
            {cohorts.map((cohort) => (
              <option key={cohort.id} value={cohort.id}>
                {cohort.name}
              </option>
            ))}
          </select>
        </label>
        <div className="rounded-md border border-dashed border-border-strong bg-surface-muted p-4">
          <UploadCloud size={22} className="mb-3 text-interactive" />
          <p className="text-sm font-medium text-content-primary">Choose CSV file</p>
          <p className="mt-1 text-xs leading-5 text-content-secondary">
            Required: code, name. Optional: center_code, is_center. District and cohort come from
            the selections above; center_code must match an existing centre.
          </p>
          <button
            type="button"
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold text-content-primary shadow-sm hover:bg-white"
            onClick={() => downloadCsvTemplate("schools")}
          >
            <Download size={15} />
            Download schools CSV template
          </button>
          <input
            className="mt-4 block w-full text-xs text-content-secondary file:mr-3 file:rounded-md file:border file:border-border file:bg-surface file:px-3 file:py-2 file:text-xs file:font-semibold file:text-content-primary hover:file:bg-white"
            name="file"
            type="file"
            accept=".csv,text/csv"
            required
          />
        </div>
        <button className={primaryButtonClass} disabled={busy}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
          Import schools
        </button>
      </form>
      {result && (
        <p className="mt-3 flex items-center gap-1.5 rounded-md bg-success-bg px-3 py-2 text-sm text-success-text">
          <CheckCircle2 size={16} className="shrink-0" />
          {result.accepted} accepted · {result.rejected.length} rejected
        </p>
      )}
      {error && (
        <p className="mt-3 flex items-center gap-1.5 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger">
          <AlertTriangle size={16} className="shrink-0" />
          {error}
        </p>
      )}
    </Card>
  );
}
